import React, { useEffect, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';

const stats = [
  { value: 150000, label: 'Lives Protected', suffix: '+' },
  { value: 500, label: 'Communities Served', suffix: '+' },
  { value: 24, label: 'Hours Monitoring', suffix: '/7' },
  { value: 99.9, label: 'Uptime Reliability', suffix: '%' },
];

const StatsSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });

  return (
    <section ref={ref} className="py-20 px-4 bg-dark-800/30">
      <div className="container mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Trusted by Communities Worldwide
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Our impact speaks for itself - real results in disaster preparedness and community resilience.
          </p>
        </motion.div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          {stats.map((stat, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={isInView ? { opacity: 1, scale: 1 } : {}}
              transition={{ duration: 0.6, delay: index * 0.2 }}
              className="text-center group"
            >
              <div className="bg-dark-700/50 backdrop-blur-sm border border-dark-600 rounded-2xl p-6 group-hover:border-primary-400/50 transition-all duration-300">
                <CountUpNumber 
                  end={stat.value} 
                  isInView={isInView}
                  suffix={stat.suffix}
                />
                <p className="text-gray-300 mt-2 font-medium">{stat.label}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

const CountUpNumber = ({ end, isInView, suffix }: { end: number; isInView: boolean; suffix: string }) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!isInView) return;

    const duration = 2000;
    const increment = end / (duration / 16);
    let current = 0;

    const timer = setInterval(() => {
      current += increment;
      if (current >= end) {
        setCount(end);
        clearInterval(timer);
      } else {
        setCount(Math.floor(current));
      }
    }, 16);

    return () => clearInterval(timer);
  }, [end, isInView]);

  const formatNumber = (num: number) => {
    if (num >= 1000) {
      return (num / 1000).toFixed(num >= 10000 ? 0 : 1) + 'k';
    }
    return num.toString();
  };

  return (
    <div className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-primary-400 to-primary-500 bg-clip-text text-transparent">
      {formatNumber(count)}{suffix}
    </div>
  );
};

export default StatsSection;